import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Tooltip } from '@material-ui/core';
import { colors } from '@/styles';
import ForwardStockingTag from '@/components/misc/ForwardStockingTag';
import { expectedArrival, isNotDueYet, isOnlySingleShipperType, receivedAt } from '../utils';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

// Date-only values only. They are stored as midnight UTC, so formatting them locally shows the
// previous day west of GMT.
const dateShort = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
});

// received_date is a real instant (`NOW.toISOString()` in onward-core `advance`), so it renders
// local: in UTC an 8:30 PM CDT receive reads as the next day. Clock time included to disambiguate.
const instantShort = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
});

// The row's expand/collapse chevron rides along in a data cell rather than a column of its own.
// Only one column may carry it, so `showToggle` hands ownership to whichever renders last.
const ExpandableCell = ({ row, callbacks, showToggle = true, children }) => (
    <div
        css={css`
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            width: 100%;
        `}
    >
        {children}
        {showToggle && row?.canExpand && (
            <span
                onClick={() => callbacks.toggleRow()}
                css={css`
                    cursor: pointer;
                    margin-right: 20px;
                `}
            >
                {row.isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowLeftIcon />}
            </span>
        )}
    </div>
);

// Orders expected after `manifestReceivingDate` stay selectable — ops legitimately pre-builds — but
// they get flagged, because picking one silently is what manifests freight days before it lands.
export const useTableColumns = (tab, manifestReceivingDate, manifestType) => {
    return useMemo(() => {
        // Single-shipper types scope the whole picker to one shipper, so the column would just
        // repeat it. CROSS_DOCK is multi-shipper and needs it, Est. Received Date and all.
        const showShipper = !isOnlySingleShipperType(manifestType);

        return [
            {
                Header: 'Create Manifest',
                id: 'create-manifest-table',
                columns: [
                    {
                        Header: 'Order',
                        id: 'order_number',
                        width: 150,
                        minWidth: 72,
                        accessor: (order) => order.order_number,
                        Cell: ({ value, row }) => (
                            <div
                                css={css`
                                    display: flex;
                                    flex-wrap: wrap;
                                    align-items: center;
                                    gap: 4px 8px;
                                `}
                            >
                                <span>{value}</span>
                                <ForwardStockingTag order={row?.original} short />
                            </div>
                        ),
                    },
                    {
                        Header: 'PO',
                        id: 'po_number',
                        width: 125,
                        minWidth: 80,
                        accessor: (order) => order.po_number,
                    },
                    {
                        Header: 'Reference #',
                        id: 'reference_id',
                        width: 150,
                        minWidth: 90,
                        accessor: (order) => order.reference_id,
                        Cell: ({ value }) => {
                            return value ? value : '-';
                        },
                    },
                    {
                        Header: 'Customer',
                        id: 'customer',
                        width: 150,
                        minWidth: 100,
                        accessor: (order) => order.dropoff_name || '',
                        Cell: ({ value }) => {
                            return value ? value : '-';
                        },
                    },

                    ...(tab.value === 'INBOUND'
                        ? [
                              {
                                  Header: 'Est. Received Date',
                                  id: 'est_received_date',
                                  width: 160,
                                  minWidth: 150,
                                  accessor: (order) => {
                                      const expected = expectedArrival(order);
                                      return expected ? new Date(expected) : null;
                                  },
                                  // Dateless orders sort first in BOTH directions — they are the
                                  // ones needing a call to the shipper. react-table negates this
                                  // result when descending (`desc ? -sortInt : sortInt` in
                                  // useSortBy), so the missing-date sign flips to survive that.
                                  sortType: (a, b, _columnId, desc) => {
                                      const left = a.values?.est_received_date?.getTime?.() ?? null;
                                      const right = b.values?.est_received_date?.getTime?.() ?? null;
                                      const datelessFirst = desc ? 1 : -1;
                                      if (left === null && right === null) return 0;
                                      if (left === null) return datelessFirst;
                                      if (right === null) return -datelessFirst;
                                      return left - right;
                                  },
                                  Cell: ({ value, callbacks, row }) => {
                                      const order = row?.original;
                                      const received = receivedAt(order);
                                      const notDueYet = isNotDueYet(order, manifestReceivingDate);

                                      let body;
                                      if (!value) {
                                          body = (
                                              <Tooltip title="No expected arrival date on this order — confirm with the shipper before manifesting it.">
                                                  <span
                                                      css={css`
                                                          color: ${colors.greys.subtitle};
                                                      `}
                                                  >
                                                      No date
                                                  </span>
                                              </Tooltip>
                                          );
                                      } else if (received) {
                                          body = (
                                              <Tooltip title={`Received ${instantShort.format(new Date(received))}`}>
                                                  <span
                                                      css={css`
                                                          color: ${colors.greens.primary};
                                                      `}
                                                  >
                                                      {dateShort.format(value)}
                                                  </span>
                                              </Tooltip>
                                          );
                                      } else if (notDueYet) {
                                          body = (
                                              <Tooltip
                                                  title={`Not due until ${dateShort.format(
                                                      value
                                                  )} — after this manifest's receiving date.`}
                                              >
                                                  <span
                                                      css={css`
                                                          color: ${colors.golds.primary};
                                                          font-weight: 600;
                                                      `}
                                                  >
                                                      {dateShort.format(value)}
                                                  </span>
                                              </Tooltip>
                                          );
                                      } else {
                                          body = <span>{dateShort.format(value)}</span>;
                                      }

                                      return (
                                          <ExpandableCell row={row} callbacks={callbacks} showToggle={!showShipper}>
                                              {body}
                                          </ExpandableCell>
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(showShipper
                        ? [
                              {
                                  Header: 'Shipper',
                                  id: 'shipper',
                                  width: 150,
                                  minWidth: 110,
                                  accessor: (order) => order?.order_shipper?.business_name || '',
                                  Cell: ({ value, callbacks, row }) => {
                                      return (
                                          <ExpandableCell row={row} callbacks={callbacks}>
                                              <span>{value}</span>
                                          </ExpandableCell>
                                      );
                                  },
                              },
                          ]
                        : []),
                ],
            },
        ];
    }, [tab, manifestReceivingDate, manifestType]);
};
